import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Ticket, User } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import apiService from '../../services/api';
import { isTicketClosed, getStatusDisplayName } from '../../utils/statusUtils';
import TicketCard from './TicketCard';
import BoardToolbar from './BoardToolbar';

interface BoardViewProps {
  tickets: Ticket[];
  loading: boolean;
  onTicketSelect: (ticket: Ticket) => void;
  onRefresh: () => void;
  onCreateTicket?: () => void;
  lastUpdated?: Date | null;
  autoUpdating?: boolean;
}

// Define the Kanban columns in the exact order specified
const KANBAN_COLUMNS = [
  {
    id: 'new',
    title: 'New',
    statuses: ['new'],
    color: 'bg-blue-50 border-blue-200',
    headerColor: 'text-blue-700',
    description: 'Newly created tickets awaiting assignment'
  },
  {
    id: 'ai-agent',
    title: 'In Progress – AI Agent',
    statuses: ['in_progress_ai_agent'],
    color: 'bg-purple-50 border-purple-200',
    headerColor: 'text-purple-700',
    description: 'Tickets being handled by AI assistant'
  },
  {
    id: 'escalated',
    title: 'Escalated – Awaiting Agent',
    statuses: ['escalated-awaiting-agent'],
    color: 'bg-orange-50 border-orange-200',
    headerColor: 'text-orange-700',
    description: 'AI handoff - requires human agent intervention'
  },
  {
    id: 'tier',
    title: 'In Progress – Tier',
    statuses: ['in_progress_tier1', 'in_progress_tier2', 'in_progress_tier3', 'in_progress'], // Include legacy status
    color: 'bg-green-50 border-green-200',
    headerColor: 'text-green-700',
    description: 'Tickets being handled by Tier 1, 2, or 3 support agents'
  },
  {
    id: 'service',
    title: 'Service',
    statuses: ['service'],
    color: 'bg-indigo-50 border-indigo-200',
    headerColor: 'text-indigo-700',
    description: 'Active service workflow tickets'
  },
  {
    id: 'closed',
    title: 'All Closed Statuses',
    statuses: ['closed-resolved', 'closed-not-resolved', 'closed-partially-resolved', 'closed-no-feedback', 'closed-by-agent-resolved', 'abandoned'],
    color: 'bg-gray-50 border-gray-200',
    headerColor: 'text-gray-700',
    description: 'All completed and closed tickets'
  }
];

// Sortable Ticket Card Component
const SortableTicketCard: React.FC<{
  ticket: Ticket;
  columnIndex: number;
  totalColumns: number;
  onTicketSelect: (ticket: Ticket) => void;
  onRefresh: () => void;
}> = ({ ticket, columnIndex, totalColumns, onTicketSelect, onRefresh }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ticket.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 999 : 'auto',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes}
      className={`transition-all duration-200 ${
        isDragging ? 'cursor-grabbing shadow-2xl' : 'hover:shadow-md'
      }`}
    >
      <TicketCard
        ticket={ticket}
        columnIndex={columnIndex}
        totalColumns={totalColumns}
        onClick={() => onTicketSelect(ticket)}
        onRefresh={onRefresh}
        dragListeners={listeners}
        isDragging={isDragging}
      />
    </div>
  );
};

// Droppable Column Component

const DroppableColumn: React.FC<{
  column: any;
  columnIndex: number;
  totalColumns: number;
  tickets: Ticket[];
  onTicketSelect: (ticket: Ticket) => void;
  onRefresh: () => void;
  dragOverColumn: string | null;
  isValidDrop: boolean;
}> = ({ column, columnIndex, totalColumns, tickets, onTicketSelect, onRefresh, dragOverColumn, isValidDrop }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });

  const isDraggedOver = isOver || dragOverColumn === column.id;
  const dropIndicatorColor = isValidDrop 
    ? 'border-green-400 bg-green-50 dark:bg-green-900/20 dark:border-green-600'
    : 'border-red-400 bg-red-50 dark:bg-red-900/20 dark:border-red-600';

  return (
    <div 
      ref={setNodeRef}
      className={`flex flex-col rounded-lg border-2 transition-all duration-200 ${column.color} dark:bg-gray-800 dark:border-gray-600 ${
        isDraggedOver ? `${dropIndicatorColor} shadow-lg transform scale-105` : ''
      } relative`}
      style={{ minHeight: '400px' }} // Ensure minimum height for drop zone
    >
      {/* Full Column Drop Zone Overlay */}
      {isDraggedOver && (
        <div className="absolute inset-0 pointer-events-none z-10 flex items-center justify-center">
          <div className={`text-sm font-medium px-4 py-2 rounded-lg ${
            isValidDrop 
              ? 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/50' 
              : 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/50'
          }`}>
            {isValidDrop ? `Drop here to move to ${column.title}` : 'Cannot move here'}
          </div>
        </div>
      )}

      {/* Column Header - Make it part of drop zone */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-600 pointer-events-none">
        <div className="flex items-center justify-between">
          <h3 
            className={`font-semibold text-sm ${column.headerColor} dark:text-gray-100`}
            title={column.description}
          >
            {column.title}
          </h3>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${column.headerColor} bg-white dark:bg-gray-700 dark:text-gray-200`}>
            {tickets.length}
          </span>
        </div>
      </div>

      {/* Column Content */}
      <div className="flex-1 p-2 space-y-2 min-h-32">
        <SortableContext items={tickets.map(t => t.id)} strategy={verticalListSortingStrategy}>
          {tickets.map((ticket) => (
            <SortableTicketCard
              key={ticket.id}
              ticket={ticket}
              columnIndex={columnIndex}
              totalColumns={totalColumns}
              onTicketSelect={onTicketSelect}
              onRefresh={onRefresh}
            />
          ))}
        </SortableContext>
        
        {/* Drop zone indicator */}
        {isDraggedOver && (
          <div className={`mx-2 mb-2 p-4 border-2 border-dashed rounded-lg transition-all duration-200 ${
            isValidDrop 
              ? 'border-green-400 bg-green-50 dark:bg-green-900/10 text-green-700 dark:text-green-300'
              : 'border-red-400 bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-300'
          }`}>
            <div className="text-center text-sm font-medium">
              {isValidDrop ? (
                <>
                  <svg className="h-6 w-6 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Drop here to move to {column.title}
                </>
              ) : (
                <>
                  <svg className="h-6 w-6 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cannot move here
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Empty state for column - Make it droppable */}
        {tickets.length === 0 && (
          <div className="flex items-center justify-center h-24 text-gray-400 dark:text-gray-500 text-sm pointer-events-none">
            {isDraggedOver ? (
              <div className={`font-medium ${isValidDrop ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {isValidDrop ? 'Drop ticket here' : 'Cannot drop here'}
              </div>
            ) : (
              'No tickets'
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const BoardView: React.FC<BoardViewProps> = ({
  tickets,
  loading,
  onTicketSelect,
  onRefresh,
  onCreateTicket,
  lastUpdated,
  autoUpdating = false
}) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [isValidDrop, setIsValidDrop] = useState<boolean>(true);
  const [filters, setFilters] = useState({
    search: '',
    agent: ['all'],
    customerType: ['all'],
    category: ['all'],
    dateRange: ['all'],
    priority: ['all'],
    status: ['all'] // Default to show all tickets (board columns separate by status)
  });
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3, // Reduced for better responsiveness
        delay: 100, // Small delay to distinguish from clicks
        tolerance: 5,
      },
    })
  );

  // Validate if a ticket can be moved to a target column
  const validateTicketMove = useCallback((ticket: Ticket, sourceColumnId: string, targetColumnId: string): { valid: boolean; reason?: string } => {
    // Same column - always valid
    if (sourceColumnId === targetColumnId) {
      return { valid: true };
    }

    console.log('🔍 Validating ticket move:', {
      ticketId: ticket.id,
      ticketNumber: ticket.ticketNumber,
      sourceColumn: sourceColumnId,
      targetColumn: targetColumnId,
      userRole: user?.role,
      userRoleName: user?.roleName,
      userPermissions: user?.permissions,
      hasTicketsEdit: user?.permissions?.includes('tickets.edit'),
      hasTicketsReopen: user?.permissions?.includes('tickets.reopen'),
      isReopenOperation: sourceColumnId === 'closed' && targetColumnId !== 'closed'
    });

    // Prevent moving from closed back to active columns without proper permissions
    if (sourceColumnId === 'closed' && targetColumnId !== 'closed') {
      // Allow if user has tickets.edit permission, is an Admin, or has explicit tickets.reopen permission
      const canReopen = user?.permissions?.includes('tickets.edit') || 
                       user?.permissions?.includes('tickets.reopen') ||
                       user?.roleName === 'Admin' ||
                       user?.role === 'agent'; // Allow agents to reopen tickets they're working on
      
      console.log('🔓 Reopen permission check:', {
        canReopen,
        hasTicketsEdit: user?.permissions?.includes('tickets.edit'),
        hasTicketsReopen: user?.permissions?.includes('tickets.reopen'),
        isAdmin: user?.roleName === 'Admin',
        isAgent: user?.role === 'agent',
        userRole: user?.role,
        userRoleName: user?.roleName
      });
      
      if (!canReopen) {
        console.log('❌ Reopen permission denied');
        return { 
          valid: false, 
          reason: 'You do not have permission to reopen closed tickets. Please contact an administrator.' 
        };
      } else {
        console.log('✅ Reopen permission granted');
      }
    }

    // Prevent moving to service column if ticket doesn't have required fields
    if (targetColumnId === 'service') {
      if (!ticket.deviceSerialNumber) {
        return { 
          valid: false, 
          reason: 'This ticket requires a device serial number to be moved to Service workflow. Please add the serial number first.' 
        };
      }
    }

    // Relax closed column validation - allow if ticket has basic info
    if (targetColumnId === 'closed') {
      // Allow closing if ticket has at least a title and description
      if (!ticket.title || !ticket.description) {
        return { 
          valid: false, 
          reason: 'Tickets must have a title and description before being closed.' 
        };
      }
    }

    // AI Agent column restrictions - only prevent if already assigned to a different human
    if (targetColumnId === 'ai-agent') {
      // Only prevent if ticket is assigned to a human agent other than the AI
      if (ticket.agentId && ticket.agentId !== 'neuro-ai' && ticket.agentId !== user?.id) {
        return { 
          valid: false, 
          reason: 'This ticket is already assigned to another human agent.' 
        };
      }
    }

    // Tier column restrictions - be more permissive for agent workflows
    if (targetColumnId === 'tier') {
      // Allow if user has tickets.edit OR if they're an agent (more permissive)
      if (!user?.permissions?.includes('tickets.edit') && user?.role !== 'agent') {
        return { 
          valid: false, 
          reason: 'You do not have permission to assign tickets to tier agents.' 
        };
      }
    }

    // Escalated column - allow agents to escalate tickets
    if (targetColumnId === 'escalated') {
      // Allow all agents to escalate tickets
      if (user?.role !== 'agent') {
        return { 
          valid: false, 
          reason: 'Only agents can escalate tickets.' 
        };
      }
    }

    console.log('✅ Ticket move validation passed');
    return { valid: true };
  }, [user]);

  // Load user preferences on component mount
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user?.id) {
        setPreferencesLoaded(true);
        return;
      }

      try {
        // Try to load from localStorage first (for immediate feedback)
        const localKey = `board-view-preferences-${user.id}`;
        const localPrefs = localStorage.getItem(localKey);
        
        if (localPrefs) {
          const parsedPrefs = JSON.parse(localPrefs);
          console.log('📋 BoardView: Loading saved preferences:', parsedPrefs);
          
          // Ensure status filter is always valid - default to ['all'] if preferences were corrupted
          if (!parsedPrefs.status || parsedPrefs.status.length === 0) {
            parsedPrefs.status = ['all'];
            console.warn('⚠️ BoardView: Invalid status filter in preferences, defaulting to "all"');
          }
          
          setFilters(prev => ({ ...prev, ...parsedPrefs }));
        } else {
          console.log('📋 BoardView: No saved preferences, using defaults');
        }
      } catch (error) {
        console.error('Error loading board preferences:', error);
      }
      
      setPreferencesLoaded(true);
    };

    loadPreferences();
  }, [user?.id]);

  // Save preferences to localStorage when filters change
  useEffect(() => {
    if (!preferencesLoaded || !user?.id) return;

    const savePreferences = () => {
      try {
        // Validate filters before saving to prevent corrupted state
        if (!filters.status || filters.status.length === 0) {
          console.warn('⚠️ BoardView: Skipping save - invalid status filter');
          return;
        }
        
        const localKey = `board-view-preferences-${user.id}`;
        localStorage.setItem(localKey, JSON.stringify(filters));
        console.log('💾 BoardView: Saved preferences:', filters);
      } catch (error) {
        console.error('Error saving board preferences:', error);
      }
    };

    // Debounce saving
    const timeoutId = setTimeout(savePreferences, 1000);
    return () => clearTimeout(timeoutId);
  }, [filters, preferencesLoaded, user?.id]);

  // Filter tickets based on current filters
  const filteredTickets = useMemo(() => {
    console.log('🔍 BoardView: Filtering tickets', {
      totalTickets: tickets.length,
      currentFilters: filters,
      statusFilter: filters.status
    });
    
    const filtered = tickets.filter(ticket => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          ticket.title.toLowerCase().includes(searchLower) ||
          ticket.ticketNumber.toLowerCase().includes(searchLower) ||
          ticket.customerCompany?.toLowerCase().includes(searchLower) ||
          ticket.deviceSerialNumber?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Agent filter
      if (!filters.agent.includes('all')) {
        const agentMatches = filters.agent.some(agentFilter => {
          if (agentFilter === 'unassigned') return !ticket.agentId;
          return ticket.agentId === agentFilter;
        });
        if (!agentMatches) return false;
      }

      // Customer Type filter
      if (!filters.customerType.includes('all')) {
        if (!ticket.customerType || !filters.customerType.includes(ticket.customerType)) {
          return false;
        }
      }

      // Category filter
      if (!filters.category.includes('all')) {
        if (!ticket.category || !filters.category.includes(ticket.category)) {
          return false;
        }
      }

      // Priority filter
      if (!filters.priority.includes('all')) {
        if (!ticket.priority || !filters.priority.includes(ticket.priority)) {
          return false;
        }
      }

      // Date Range filter
      if (!filters.dateRange.includes('all')) {
        const now = new Date();
        const ticketDate = new Date(ticket.createdAt);
        
        const dateMatches = filters.dateRange.some(dateFilter => {
          let startDate: Date;
          let endDate: Date = now;
          
          switch (dateFilter) {
            case 'today':
              startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              break;
            case 'yesterday':
              startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
              endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              break;
            case 'this-week':
              const dayOfWeek = now.getDay();
              startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
              break;
            case 'last-week':
              const lastWeekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 7);
              const lastWeekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
              startDate = lastWeekStart;
              endDate = lastWeekEnd;
              break;
            case 'this-month':
              startDate = new Date(now.getFullYear(), now.getMonth(), 1);
              break;
            case 'last-month':
              startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
              endDate = new Date(now.getFullYear(), now.getMonth(), 0);
              break;
            default:
              return true;
          }
          
          return ticketDate >= startDate && ticketDate <= endDate;
        });
        
        if (!dateMatches) return false;
      }

      // Status filter
      if (!filters.status.includes('all')) {
        const statusMatches = filters.status.some(statusFilter => {
          if (statusFilter === 'open') return !isTicketClosed(ticket.status);
          if (statusFilter === 'closed') return isTicketClosed(ticket.status);
          // Handle specific status values
          return ticket.status === statusFilter;
        });
        if (!statusMatches) return false;
      }

      return true;
    });
    
    console.log('✅ BoardView: Filtered tickets result', {
      filteredCount: filtered.length,
      openTickets: filtered.filter(t => !isTicketClosed(t.status)).length,
      closedTickets: filtered.filter(t => isTicketClosed(t.status)).length
    });
    
    return filtered;
  }, [tickets, filters]);

  // Group filtered tickets by column
  const ticketsByColumn = useMemo(() => {
    const grouped: { [key: string]: Ticket[] } = {};
    
    // Initialize all columns
    KANBAN_COLUMNS.forEach(column => {
      grouped[column.id] = [];
    });

    // Group tickets into appropriate columns
    filteredTickets.forEach(ticket => {
      const column = KANBAN_COLUMNS.find(col => 
        col.statuses.includes(ticket.status as any)
      );
      
      if (column) {
        grouped[column.id].push(ticket);
      } else {
        // Fallback for any unmapped statuses
        console.warn(`Unmapped ticket status: ${ticket.status}`);
        grouped['new'].push(ticket);
      }
    });

    return grouped;
  }, [filteredTickets]);

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setDragOverColumn(null);
    setIsValidDrop(true);
  }, []);

  // Handle drag over for visual feedback
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over, active } = event;
    
    if (!over || !active) {
      setDragOverColumn(null);
      setIsValidDrop(true);
      return;
    }

    const overId = over.id as string;
    const activeId = active.id as string;
    
    console.log('🎯 Drag over detected:', { 
      overId, 
      activeId,
      isOverTicket: overId === activeId,
      isOverColumn: KANBAN_COLUMNS.some(col => col.id === overId)
    });
    
    // Find the ticket being dragged from ALL tickets (not just filtered)
    const ticket = tickets.find(t => t.id === activeId);
    if (!ticket) {
      console.log('❌ Ticket not found for activeId:', activeId);
      return;
    }

    // Check if ticket is visible in current filtered view
    const ticketInFilteredView = filteredTickets.some(t => t.id === activeId);
    if (!ticketInFilteredView) {
      console.log('⚠️ Warning: Ticket being dragged is not in filtered view', {
        ticketId: activeId,
        ticketStatus: ticket.status,
        currentFilters: filters
      });
    }

    // Find source column based on ticket's actual status (not filtered view)
    const sourceColumn = KANBAN_COLUMNS.find(col => 
      col.statuses.includes(ticket.status as any)
    );
    
    // Check if overId is a column ID directly first (most reliable)
    let destColumn = KANBAN_COLUMNS.find(col => col.id === overId);
    
    if (!destColumn) {
      // If not a direct column ID, find column by looking at all tickets (not just filtered)
      destColumn = KANBAN_COLUMNS.find(col => 
        tickets.some(t => t.id === overId && col.statuses.includes(t.status as any))
      );
    }
    
    // If still not found, try looking in the ticketsByColumn (fallback)
    if (!destColumn) {
      for (const [columnId, columnTickets] of Object.entries(ticketsByColumn)) {
        if (columnTickets.some(t => t.id === overId)) {
          destColumn = KANBAN_COLUMNS.find(col => col.id === columnId);
          console.log('🔍 Found destination column via ticketsByColumn fallback:', columnId);
          break;
        }
      }
    }

    console.log('🔍 Column detection (filter-aware):', {
      ticketStatus: ticket.status,
      sourceColumn: sourceColumn?.id,
      destColumn: destColumn?.id,
      overId,
      availableColumns: KANBAN_COLUMNS.map(c => c.id),
      isFiltered: filteredTickets.length !== tickets.length,
      currentFilters: filters,
      ticketInFilteredList: filteredTickets.some(t => t.id === activeId),
      ticketsByColumnKeys: Object.keys(ticketsByColumn),
      ticketInTicketsByColumn: Object.values(ticketsByColumn).flat().some(t => t.id === activeId),
      totalTickets: tickets.length,
      filteredTicketsCount: filteredTickets.length,
      statusFilter: filters.status,
      escalatedColumnTickets: ticketsByColumn['escalated']?.length || 0,
      escalatedColumnExists: !!ticketsByColumn['escalated']
    });

    if (!sourceColumn || !destColumn) {
      console.log('❌ Column not found');
      return;
    }

    // Validate the move
    const validation = validateTicketMove(ticket, sourceColumn.id, destColumn.id);
    
    // Special debugging for escalated column issues
    if (destColumn.id === 'escalated') {
      console.log('🚨 ESCALATED COLUMN DEBUG:', {
        statusFilter: filters.status,
        sourceColumnId: sourceColumn.id,
        destColumnId: destColumn.id,
        validation,
        userRole: user?.role,
        userRoleName: user?.roleName,
        ticketStatus: ticket.status,
        isTicketInFilteredView: filteredTickets.some(t => t.id === activeId)
      });
    }
    
    console.log('🔍 Validation result:', validation);
    
    setDragOverColumn(destColumn.id);
    setIsValidDrop(validation.valid);
  }, [tickets, validateTicketMove, filteredTickets, filters]);

  // Handle drag end
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setDragOverColumn(null);
    setIsValidDrop(true);

    if (!over) {
      addNotification({
        type: 'system',
        title: 'Move Cancelled',
        message: 'Ticket move was cancelled',
        priority: 'low',
        duration: 3000
      });
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    // Find the ticket being moved
    const ticket = tickets.find(t => t.id === activeId);
    if (!ticket) {
      addNotification({
        type: 'system',
        title: 'Move Failed',
        message: 'Ticket not found',
        priority: 'medium',
        duration: 5000
      });
      return;
    }

    // Find source and destination columns based on actual ticket status (filter-aware)
    const sourceColumn = KANBAN_COLUMNS.find(col => 
      col.statuses.includes(ticket.status as any)
    );
    
    // Check if overId is a column ID directly first (most reliable)
    let destColumn = KANBAN_COLUMNS.find(col => col.id === overId);
    
    if (!destColumn) {
      // If not a direct column ID, find column by looking at all tickets (not just filtered)
      destColumn = KANBAN_COLUMNS.find(col => 
        tickets.some(t => t.id === overId && col.statuses.includes(t.status as any))
      );
    }
    
    // If still not found, try looking in the ticketsByColumn (fallback)
    if (!destColumn) {
      for (const [columnId, columnTickets] of Object.entries(ticketsByColumn)) {
        if (columnTickets.some(t => t.id === overId)) {
          destColumn = KANBAN_COLUMNS.find(col => col.id === columnId);
          console.log('🔍 DragEnd: Found destination column via ticketsByColumn fallback:', columnId);
          break;
        }
      }
    }

    console.log('🔚 DragEnd column detection:', {
      sourceColumn: sourceColumn?.id,
      destColumn: destColumn?.id,
      overId,
      statusFilter: filters.status
    });

    if (!sourceColumn || !destColumn) {
      addNotification({
        type: 'system',
        title: 'Move Failed',
        message: 'Invalid destination column',
        priority: 'medium',
        duration: 5000
      });
      return;
    }

    // If same column, don't change status
    if (sourceColumn.id === destColumn.id) return;

    // Validate the move
    const validation = validateTicketMove(ticket, sourceColumn.id, destColumn.id);
    if (!validation.valid) {
      addNotification({
        type: 'system',
        title: 'Move Restricted',
        message: validation.reason || 'This move is not allowed',
        priority: 'high',
        duration: 7000
      });
      return;
    }

    // Show confirmation for certain sensitive moves
    const needsConfirmation = (
      (sourceColumn.id !== 'closed' && destColumn.id === 'closed') || // Moving to closed
      (sourceColumn.id === 'closed' && destColumn.id !== 'closed') || // Reopening
      (destColumn.id === 'service') // Moving to service
    );

    if (needsConfirmation) {
      const confirmationMessages = {
        'closed': 'Are you sure you want to close this ticket? This will mark it as resolved.',
        'service': 'Moving this ticket to Service will initiate a service workflow. Continue?',
        'reopen': 'Are you sure you want to reopen this closed ticket?'
      };
      
      const messageKey = destColumn.id === 'closed' ? 'closed' : 
                        destColumn.id === 'service' ? 'service' : 'reopen';
      
      const confirmed = window.confirm(confirmationMessages[messageKey]);
      if (!confirmed) {
        addNotification({
          type: 'system',
          title: 'Move Cancelled',
          message: 'Ticket move was cancelled by user',
          priority: 'low',
          duration: 3000
        });
        return;
      }
    }

    // Determine the new status based on destination column
    let newStatus = destColumn.statuses[0]; // Use first status as default
    
    // Special handling for different column types
    if (destColumn.id === 'tier') {
      // When dropping into the consolidated tier column, default to tier 1
      // unless the ticket is already in a tier status (preserve tier level)
      const currentTier = ['in_progress_tier1', 'in_progress_tier2', 'in_progress_tier3'];
      if (currentTier.includes(ticket.status as any)) {
        newStatus = ticket.status; // Keep current tier level
      } else {
        newStatus = 'in_progress_tier1'; // Default to tier 1 for new assignments
      }
    } else if (destColumn.id === 'ai-agent') {
      newStatus = 'in_progress_ai_agent';
    } else if (destColumn.id === 'escalated') {
      newStatus = 'escalated-awaiting-agent';
    } else if (destColumn.id === 'service') {
      newStatus = 'service';
    } else if (destColumn.id === 'closed') {
      newStatus = 'closed-resolved'; // Default to resolved when moving to closed
    }

    try {
      // Show loading notification
      addNotification({
        type: 'system',
        title: 'Moving Ticket',
        message: `Moving ticket to ${destColumn.title}...`,
        priority: 'low',
        duration: 2000
      });

      // Update ticket status via API
      await apiService.updateTicket(ticket.id, { status: newStatus as any });
      
      // Show success notification
      addNotification({
        type: 'system',
        title: 'Ticket Moved',
        message: `Ticket ${ticket.ticketNumber} successfully moved to ${destColumn.title}`,
        priority: 'medium',
        duration: 4000
      });
      
      // Refresh the ticket list
      onRefresh();
    } catch (error) {
      console.error('Error updating ticket status:', error);
      addNotification({
        type: 'system',
        title: 'Move Failed',
        message: `Failed to move ticket: ${error instanceof Error ? error.message : 'Unknown error'}`,
        priority: 'high',
        duration: 6000
      });
    }
  }, [tickets, onRefresh, validateTicketMove, addNotification]);

  const handleFiltersChange = useCallback((newFilters: any) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    
    const now = Date.now();
    const diffMs = now - lastUpdated.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    
    if (diffSeconds < 60) {
      return `Updated ${diffSeconds}s ago`;
    } else if (diffMinutes < 60) {
      return `Updated ${diffMinutes}m ago`;
    } else {
      return `Updated at ${lastUpdated.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    }
  };

  if (loading) {
    return (
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded-lg mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-96 bg-gray-50 dark:bg-gray-800 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-3 sm:px-4 lg:px-6">
      {/* Compact Header - Single Row */}
      <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 mb-3">
        {/* Left: Title and Stats */}
        <div className="flex items-center space-x-4">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">
            Board View
          </h1>
          <div className="flex items-center space-x-3 text-xs">
            <span className="text-gray-600 dark:text-gray-400 font-medium">
              {filteredTickets.length} of {tickets.length}
            </span>
            {lastUpdated && (
              <span className="text-green-600 dark:text-green-400 flex items-center">
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {formatLastUpdated()}
              </span>
            )}
            {autoUpdating && (
              <span className="text-blue-600 dark:text-blue-400 flex items-center">
                <svg className="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">Syncing...</span>
              </span>
            )}
          </div>
        </div>

        {/* Right: Action Buttons */}
        <div className="flex items-center space-x-2">
          {user?.role === 'agent' && user?.permissions?.includes('tickets.create') && onCreateTicket && (
            <button
              onClick={onCreateTicket}
              className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors shadow-sm"
              title="Create New Ticket"
            >
              <svg className="h-4 w-4 sm:mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">Create</span>
            </button>
          )}
          <button
            onClick={onRefresh}
            disabled={loading}
            className={`inline-flex items-center px-3 py-1.5 border rounded-md text-xs font-medium transition-all shadow-sm ${
              loading
                ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                : 'border-green-500 dark:border-green-600 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
            }`}
            title="Force Refresh Board"
          >
            <svg className={`h-4 w-4 sm:mr-1.5 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">{loading ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Compact Filters Toolbar */}
      <BoardToolbar 
        filters={filters}
        onFiltersChange={handleFiltersChange}
        totalTickets={tickets.length}
        filteredTickets={filteredTickets.length}
      />

      {/* Kanban Board - Full Screen */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2 pb-4">
          {KANBAN_COLUMNS.map((column, index) => (
            <DroppableColumn
              key={column.id}
              column={column}
              columnIndex={index}
              totalColumns={KANBAN_COLUMNS.length}
              tickets={ticketsByColumn[column.id] || []}
              onTicketSelect={onTicketSelect}
              onRefresh={onRefresh}
              dragOverColumn={dragOverColumn}
              isValidDrop={isValidDrop}
            />
          ))}
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeId ? (
            <div className="rotate-2 shadow-2xl opacity-90 transform scale-105 transition-transform duration-200">
              <TicketCard
                ticket={tickets.find(t => t.id === activeId)!}
                onClick={() => {}}
                onRefresh={onRefresh}
              />
              {!isValidDrop && (
                <div className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default BoardView;
